import React, { PropsWithChildren, useEffect, useRef } from 'react';
import {
  APILoadingStatus,
  Map,
  MapCameraChangedEvent,
  MapEvent,
  RenderingType,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps';

import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { LatLng } from '@rover/types/src/LatLng';

import { GeoMapLoadErrorEvent } from './analyticsEvents';
import { DEFAULT_MAP_ZOOM, GestureHandling } from './types';

// this is the map id saved in GCP Google Maps Management - this ID is associated with a custom map style.
export const GEOMAP_CLOUD_ID = 'a6ce008e2463c8de481e0d2a';

export interface GeoMapProps extends PropsWithChildren {
  // Map configuration
  id?: string;
  center?: LatLng<number>;
  zoom?: number | null;

  // Map controls
  disableDefaultUI?: boolean;
  disableDoubleClickZoom?: boolean;
  draggable?: boolean;
  gestureHandling?: GestureHandling;
  mapTypeControl?: boolean;
  maxZoom?: number;
  minZoom?: number;
  rotateControl?: boolean;
  scaleControl?: boolean;
  scrollwheel?: boolean;
  streetViewControl?: boolean;
  zoomControl?: boolean;
  clickableIcons?: boolean;

  // Event handlers
  onIdle?: (event: MapEvent) => void;
  onCenterChanged?: (center: MapCameraChangedEvent) => void;
  onZoomChanged?: (metadata: MapCameraChangedEvent) => void;
  onTilesLoaded?: (event: MapEvent) => void;

  // Children and styling
  className?: string;
  innerRef?: (element: HTMLDivElement | null) => void;
}

const formatCoordinate = (coordinate: string | number): number => parseFloat(coordinate.toString());

const parseCenter = (center?: LatLng | null): LatLng<number> | undefined => {
  if (!center) return undefined;
  return {
    lat: formatCoordinate(center.lat),
    lng: formatCoordinate(center.lng),
  };
};

const GeoMap = ({
  id = GEOMAP_CLOUD_ID,
  center: centerProp,
  zoom: zoomProp,
  disableDefaultUI = false,
  disableDoubleClickZoom = false,
  draggable = true,
  gestureHandling = 'cooperative',
  maxZoom = 16,
  minZoom = 8,
  scaleControl = false,
  scrollwheel = false,
  zoomControl = false,
  clickableIcons = false,
  onIdle,
  onZoomChanged,
  onCenterChanged,
  onTilesLoaded,
  children,
}: GeoMapProps): JSX.Element => {
  const map = useMap();
  const loadingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const status = useApiLoadingStatus();

  const clearTimer = (): void => {
    loadingTimeout?.current && clearInterval(loadingTimeout.current);
  };

  useEffect((): void => {
    const mapLoadFailed = (): void => {
      clearTimer();
      emitAnalyticsEvent(
        new GeoMapLoadErrorEvent({
          error: 'timeout',
        })
      );
    };
    if (status === APILoadingStatus.FAILED || status === APILoadingStatus.AUTH_FAILURE) {
      mapLoadFailed();
    } else if (status === APILoadingStatus.LOADING && !loadingTimeout?.current) {
      loadingTimeout.current = setTimeout(() => mapLoadFailed(), 10000);
    } else if (status === APILoadingStatus.LOADED) {
      clearTimer();
    }
  }, [status]);

  useEffect(() => {
    // clear on component unmount
    return () => {
      clearTimer();
    };
  }, []);

  const defaultMapConfig = {
    renderingType: 'VECTOR' as RenderingType,
    mapTypeControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    tiltInteractionEnabled: false,
  };

  useEffect(() => {
    const parsedCenter = parseCenter(centerProp);
    if (centerProp && map && parsedCenter) {
      map.setCenter(parsedCenter);
    }
  }, [centerProp, map]);

  return (
    <Map
      mapId={id}
      style={{ width: '100%', height: '100%', position: 'absolute', zIndex: 0 }}
      defaultCenter={parseCenter(centerProp)}
      defaultZoom={zoomProp || DEFAULT_MAP_ZOOM}
      disableDefaultUI={disableDefaultUI}
      disableDoubleClickZoom={disableDoubleClickZoom}
      draggable={draggable}
      gestureHandling={gestureHandling}
      maxZoom={maxZoom}
      minZoom={minZoom}
      scaleControl={scaleControl}
      scrollwheel={scrollwheel}
      zoomControl={zoomControl}
      clickableIcons={clickableIcons}
      onZoomChanged={onZoomChanged}
      onCenterChanged={(event: MapCameraChangedEvent) => onCenterChanged && onCenterChanged(event)}
      onTilesLoaded={(event: MapEvent) => onTilesLoaded && onTilesLoaded(event)}
      onIdle={onIdle}
      {...defaultMapConfig}
    >
      {children}
    </Map>
  );
};

export default GeoMap;
